import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth, UserRole, RoleLabels } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useGpsSharing } from '../context/GpsSharingContext';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from '../components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Shield,
    LayoutDashboard,
    AlertTriangle,
    Package,
    Users,
    Clock,
    Building2,
    UserCog,
    Bell,
    LogOut,
    Menu,
    ChevronLeft,
    ChevronRight,
    User,
    Truck,
    ClipboardList,
    Home,
    Phone,
    MapPin,
    Target,
    GitBranch,
    Sun,
    Moon,
    BookOpen,
    FileText,
    Navigation,
    Map,
    Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Sidebar = ({ collapsed, setCollapsed }) => {
    const { user, token, isChefDispositif, isLogistique, isMoyen, isGymnaseChefPoste, isGymnaseAdmin, isCommune, logout } = useAuth();
    const { unreadCount } = useNotifications();
    const gpsSharing = useGpsSharing();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [careCenters, setCareCenters] = useState([]);
    const [selectedCenter, setSelectedCenter] = useState(user?.assigned_center_id || '');

    const isGymnaseUser = user?.role === UserRole.GYMNASE_ADMIN || user?.role === UserRole.GYMNASE_CHEF_POSTE;

    useEffect(() => {
        if (isGymnaseUser && token) {
            axios.get(`${API}/care-centers`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => setCareCenters(res.data))
                .catch(() => {});
        }
    }, [isGymnaseUser, token]);

    useEffect(() => {
        setSelectedCenter(user?.assigned_center_id || '');
    }, [user?.assigned_center_id]);

    const handleCenterChange = async (centerId) => {
        try {
            await axios.put(`${API}/users/me/center?center_id=${centerId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
            setSelectedCenter(centerId);
            const centerName = careCenters.find(c => c.id === centerId)?.name;
            toast.success(`Affecté au centre "${centerName}"`);
            // Refresh user data
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de centre');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const navItems = [
        { 
            to: '/dashboard', 
            icon: LayoutDashboard, 
            label: 'Tableau de bord',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]
        },
        { 
            to: '/incidents', 
            icon: AlertTriangle, 
            label: 'Incidents',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.MOYEN, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS]
        },
        {
            to: '/care-centers',
            icon: Home,
            label: 'Centres CARE',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.RENFORT_CARE, UserRole.SOUTIEN_PSY]
        },
        { 
            to: '/resources', 
            icon: Package, 
            label: 'Ressources',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.SOUTIEN_LOG_COMMUNE]
        },
        {
            to: '/material-requests',
            icon: ClipboardList,
            label: 'Demandes Materiel',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.GYMNASE_CHEF_POSTE, UserRole.SOUTIEN_LOG_COMMUNE]
        },
        { 
            to: '/persons', 
            icon: Users, 
            label: 'Personnes',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.RENFORT_CARE, UserRole.SOUTIEN_PSY]
        },
        {
            to: '/vehicles',
            icon: Truck,
            label: 'Vehicules',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.MOYEN, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS]
        },
        {
            to: '/carte',
            icon: Map,
            label: 'Carte',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.CHAUFFEUR_PL]
        },
        {
            to: '/ordres-mission',
            icon: ClipboardList,
            label: 'Ordres de Mission',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.COMMUNE, UserRole.CHAUFFEUR_PL]
        },
        {
            to: '/chauffeur',
            icon: Navigation,
            label: 'Mode Chauffeur',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.CHAUFFEUR_PL, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]
        },
        { 
            to: '/shifts', 
            icon: Clock, 
            label: 'Personnel',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.GYMNASE_CHEF_POSTE, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]
        },
        {
            to: '/crisis-directory',
            icon: Phone,
            label: 'Annuaire Crise',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.CHAUFFEUR_PL, UserRole.SOUTIEN_PSY]
        },
        {
            to: '/missions',
            icon: Target,
            label: 'Retour normalite',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]
        },
        {
            to: '/organigramme',
            icon: GitBranch,
            label: 'Organigramme',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.SECOURISTE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS, UserRole.RENFORT_CARE, UserRole.OPERATEUR_RADIO, UserRole.SOUTIEN_PSY]
        },
        {
            to: '/procedures',
            icon: BookOpen,
            label: 'Procedures',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.MOYEN, UserRole.GYMNASE_CHEF_POSTE, UserRole.GYMNASE_ADMIN, UserRole.COMMUNE, UserRole.EQUIPIER, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION, UserRole.POLICE_MUNICIPALE, UserRole.COS]
        },
        {
            to: '/documents',
            icon: FileText,
            label: 'Documents',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.LOGISTIQUE, UserRole.COMMUNE, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION]
        },
        { 
            to: '/commune', 
            icon: Building2, 
            label: 'Dashboard Commune',
            roles: [UserRole.CHEF_DISPOSITIF, UserRole.COMMUNE, UserRole.SOUTIEN_LOG_COMMUNE, UserRole.SECRETARIAT_COMMUNICATION]
        },
        { 
            to: '/users', 
            icon: UserCog, 
            label: 'Utilisateurs',
            roles: [UserRole.CHEF_DISPOSITIF]
        },
        {
            to: '/parametres',
            icon: Settings,
            label: 'Parametres',
            roles: [UserRole.CHEF_DISPOSITIF]
        },
    ];

    const filteredNavItems = navItems.filter(item => 
        item.roles.includes(user?.role)
    );

    const NavContent = () => (
        <>
            {/* Header */}
            <div className="sidebar-header">
                <div className="sidebar-logo">
                    {!collapsed ? (
                        <img src="/civis-logo.png" alt="CIVIS+" className="h-16 w-auto" data-testid="sidebar-logo" />
                    ) : (
                        <button onClick={() => setCollapsed(false)} className="w-full text-center text-xl font-black text-white hover:opacity-80 transition-opacity" title="Agrandir le menu">
                            C<span className="text-red-500">+</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <nav className="sidebar-nav">
                <div className="space-y-1">
                    {filteredNavItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            title={collapsed ? item.label : undefined}
                            className={({ isActive }) =>
                                `sidebar-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-2' : ''}`
                            }
                            data-testid={`nav-${item.to.replace('/', '')}`}
                        >
                            <item.icon className={collapsed ? 'w-6 h-6' : 'w-5 h-5'} />
                            {!collapsed && <span>{item.label}</span>}
                            {item.to === '/chauffeur' && gpsSharing?.sharing && (
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0 ml-auto" title="Partage GPS actif" data-testid="gps-sharing-indicator"></span>
                            )}
                        </NavLink>
                    ))}
                </div>

                {/* Center selector for Gymnase users */}
                {isGymnaseUser && !collapsed && careCenters.length > 0 && (
                    <div className="mt-4 mx-2 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50" data-testid="center-selector">
                        <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs font-medium text-zinc-400">Centre affecté</span>
                        </div>
                        <Select value={selectedCenter} onValueChange={handleCenterChange}>
                            <SelectTrigger className="w-full bg-zinc-900 border-zinc-700 text-sm h-8">
                                <SelectValue placeholder="Choisir un centre" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                {careCenters.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </nav>

            {/* Footer */}
            <div className="sidebar-footer">
                <div className="flex items-center justify-between mb-4">
                    {!collapsed && (
                        <NavLink to="/profile" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="profile-link">
                            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                                <User className="w-4 h-4 text-zinc-400" />
                            </div>
                            <div className="text-sm">
                                <p className="text-white font-medium truncate max-w-[120px]">{user?.name}</p>
                                <p className="text-xs text-zinc-500">{RoleLabels[user?.role] || user?.role}</p>
                            </div>
                        </NavLink>
                    )}
                    
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button 
                                variant="ghost" 
                                size="sm"
                                className="relative"
                                data-testid="notifications-btn"
                            >
                                <Bell className="w-5 h-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                                        {unreadCount}
                                    </span>
                                )}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64 bg-zinc-900 border-zinc-800">
                            <div className="p-2">
                                <p className="text-sm font-semibold text-white">Notifications</p>
                            </div>
                            <DropdownMenuSeparator className="bg-zinc-800" />
                            <div className="p-4 text-center text-zinc-500 text-sm">
                                {unreadCount > 0 
                                    ? `${unreadCount} notification(s) non lue(s)` 
                                    : 'Aucune notification'
                                }
                            </div>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <Button
                    variant="ghost"
                    className={`w-full ${collapsed ? 'justify-center px-2' : 'justify-start'} text-zinc-400 hover:text-white hover:bg-zinc-800`}
                    onClick={handleLogout}
                    title={collapsed ? 'Deconnexion' : undefined}
                    data-testid="logout-btn"
                >
                    <LogOut className={collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-2'} />
                    {!collapsed && 'Deconnexion'}
                </Button>

                <Button
                    variant="ghost"
                    className={`w-full ${collapsed ? 'justify-center px-2' : 'justify-start'} text-zinc-400 hover:text-white hover:bg-zinc-800 mt-1`}
                    onClick={toggleTheme}
                    title={collapsed ? (theme === 'dark' ? 'Mode Jour' : 'Mode Nuit') : undefined}
                    data-testid="theme-toggle-btn"
                >
                    {theme === 'dark' ? (
                        <><Sun className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-2'} text-amber-400`} />{!collapsed && 'Mode Jour'}</>
                    ) : (
                        <><Moon className={`${collapsed ? 'w-6 h-6' : 'w-5 h-5 mr-2'} text-blue-400`} />{!collapsed && 'Mode Nuit'}</>
                    )}
                </Button>

                {!collapsed ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2 text-zinc-500"
                        onClick={() => setCollapsed(true)}
                        data-testid="sidebar-collapse-btn"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Réduire
                    </Button>
                ) : (
                    <Button
                        variant="ghost"
                        className="w-full mt-2 justify-center px-2 text-zinc-400 hover:text-white hover:bg-zinc-800"
                        onClick={() => setCollapsed(false)}
                        title="Agrandir le menu"
                        data-testid="sidebar-expand-btn"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </Button>
                )}
            </div>
        </>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <aside className={`hidden md:flex flex-col w-64 bg-zinc-950 border-r border-zinc-800 fixed h-screen z-50 ${collapsed ? 'sidebar-collapsed' : ''}`}>
                <NavContent />
            </aside>

            {/* Mobile Top Bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-950 border-b border-zinc-800 px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img src="/civis-logo.png" alt="CIVIS+" className="h-7" />
                    </div>
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon">
                                <Menu className="w-6 h-6" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-64 bg-zinc-950 border-zinc-800 p-0">
                            <NavContent />
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
